/**
 * Version history for one note: list on the left, selected snapshot on the
 * right, restore in one click. The server keeps the last N versions of every
 * note (see docs/sync-protocol.md §3) — this is the reader for that log.
 */
import { useEffect, useState } from 'react';
import { History, RotateCcw } from 'lucide-react';
import {
  syncApi,
  type NoteVersionDetail,
  type NoteVersionSummary,
} from '../lib/api/sync.js';
import { ApiError } from '../lib/api/client.js';
import { useOverlayScrollbar } from '../lib/ui/overlayScroll.js';

interface VersionHistoryDialogProps {
  noteId: string;
  onRestored: (noteId: string) => Promise<void> | void;
  onClose: () => void;
}

const ORIGIN_LABELS: Record<string, string> = {
  edit: 'Edit',
  conflict_loser: 'Conflict (overwritten)',
  restore: 'Restored',
  migration: 'Imported',
};

export function VersionHistoryDialog({ noteId, onRestored, onClose }: VersionHistoryDialogProps) {
  const [versions, setVersions] = useState<NoteVersionSummary[] | null>(null);
  const [selected, setSelected] = useState<NoteVersionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  // Floating capsule scrollbars (macOS overlay parity) — see lib/ui/overlayScroll.ts.
  const modalScroll = useOverlayScrollbar<HTMLDivElement>();
  const listScroll = useOverlayScrollbar<HTMLUListElement>();
  const previewScroll = useOverlayScrollbar<HTMLPreElement>();

  useEffect(() => {
    let cancelled = false;
    syncApi
      .noteVersions(noteId)
      .then((result) => {
        if (!cancelled) setVersions(result.versions);
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(cause instanceof ApiError ? cause.message : 'Could not load versions.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [noteId]);

  async function openVersion(version: NoteVersionSummary): Promise<void> {
    setError(null);
    try {
      const { version: detail } = await syncApi.noteVersion(noteId, version.id);
      setSelected(detail);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not load this version.');
    }
  }

  async function restore(): Promise<void> {
    if (!selected) return;
    setRestoring(true);
    try {
      await syncApi.restoreVersion(noteId, selected.id);
      await onRestored(noteId);
      onClose();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not restore this version.');
    } finally {
      setRestoring(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="ob-host ob-host-modal" ref={modalScroll.hostRef}>
        <div
          className="modal modal-wide"
          role="dialog"
          aria-label="Version history"
          onClick={(event) => event.stopPropagation()}
          ref={modalScroll.scrollerRef}
        >
        <div className="modal-header">
          <History size={15} strokeWidth={1.75} />
          <span>Version history</span>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="modal-body">
          {error && <div className="modal-error" role="alert">{error}</div>}
          {versions === null && !error && <p className="modal-text">Loading versions…</p>}
          {versions !== null && versions.length === 0 && (
            <p className="modal-text">No earlier versions of this note yet.</p>
          )}
          {versions !== null && versions.length > 0 && (
            <div className="version-grid">
              <div className="ob-host" ref={listScroll.hostRef}>
                <ul className="version-list" ref={listScroll.scrollerRef}>
                  {versions.map((version) => (
                    <li key={version.id}>
                      <button
                        type="button"
                        className={`version-item ${selected?.id === version.id ? 'is-selected' : ''}`}
                        onClick={() => void openVersion(version)}
                      >
                        <span className="version-title">{version.title || 'Untitled'}</span>
                        <span className="version-meta">
                          v{version.version} · {ORIGIN_LABELS[version.origin] ?? version.origin} ·{' '}
                          {new Date(version.createdAt).toLocaleString()}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <section className="version-preview">
                {selected ? (
                  <>
                    <header>
                      <strong>{selected.title || 'Untitled'}</strong>
                      <span>
                        v{selected.version} · {new Date(selected.createdAt).toLocaleString()}
                      </span>
                    </header>
                    <div className="ob-host ob-host-pre" ref={previewScroll.hostRef}>
                      <pre ref={previewScroll.scrollerRef}>{selected.content}</pre>
                    </div>
                    <div className="modal-actions">
                      <button
                        type="button"
                        className="button button-small"
                        disabled={restoring}
                        onClick={() => void restore()}
                      >
                        <RotateCcw size={13} strokeWidth={1.75} />
                        Restore this version
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="modal-text">Select a version to preview it.</p>
                )}
              </section>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
